import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';

/**
 * Email verification page component
 * Handles verification of user email with token from URL
 */
const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Function to verify the email token
    const verifyEmail = async () => {
      try {
        // Make sure token exists
        if (!token) {
          setError('Verification token is missing');
          setVerifying(false);
          return;
        }

        // Call API to verify the token
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify/${token}`);
        
        if (response.ok) {
          setVerified(true);
          // Redirect to login page after 3 seconds
          setTimeout(() => {
            navigate('https://edx.red/login');
          }, 3000);
        } else {
          const data = await response.json();
          setError(data.error || 'Failed to verify email');
        }
      } catch (error) {
        console.error('Error verifying email:', error);
        setError('An error occurred while verifying your email');
      } finally {
        setVerifying(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Verifying your email</h1>
            <div className="mt-4 flex justify-center">
              <div className="animate-spin h-8 w-8 border-4 border-red-500 rounded-full border-t-transparent"></div>
            </div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Please wait while we verify your email address...</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Email Verified Successfully!</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Your email has been verified. You can now log in to your account.
            </p>
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              Redirecting to login page...
            </p>
            <div className="mt-6">
              <Link 
                to="/login" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="w-full max-w-md p-8 space-y-8 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Verification Failed</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {error || 'There was a problem verifying your email address.'}
          </p>
          <div className="mt-6 space-y-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The verification link may have expired or already been used.
            </p>
            <div className="flex flex-col space-y-2">
              <Link 
                to="/login" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Go to Login
              </Link>
              <Link 
                to="/register" 
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Register Again
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;